/**
 * Typed auth + 2FA GraphQL operations.
 *
 * Contract mirrors the NestJS API (`apps/api`): a passwordless magic-link sign-in
 * emails both a click-through link and a 6-digit code. Consuming either (by code
 * or by token) branches to a server-set `ca_2fa_pending` cookie when the user has
 * 2FA on; the OTP step is then identified from that cookie (no userId travels for
 * `validateOtp`).
 */
import { gql } from '@/core/api/client';

export type User2faMethod = 'AUTHENTICATOR' | 'PASSKEY';

/** A role assigned to the current user (only `me.roles` is resolvable client-side). */
export interface AuthUserRole {
  id: string;
  title: string;
}

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  status: string;
  is2faEnabled: boolean | null;
  isOtpEnabled: boolean | null;
  preferred2faMethod: User2faMethod | null;
  otpRecoveryCodesRemaining: number;
  /**
   * Only populated by the `me` query — `User.roles` is a resolver restricted to
   * the requester themselves, so the login / OTP mutations (which run in a guest
   * context and return the just-authenticated user) must NOT select it, or it
   * throws AccessDenied. The store refreshes `me` after every sign-in so this is
   * present whenever a session is live.
   */
  roles?: AuthUserRole[];
}

const AUTH_USER_FRAGMENT = /* GraphQL */ `
  fragment AuthUser on User {
    id
    email
    firstName
    lastName
    fullName
    status
    is2faEnabled
    isOtpEnabled
    preferred2faMethod
    otpRecoveryCodesRemaining
  }
`;

/** Result of consuming a magic link — by 6-digit code or by click-through token. */
export interface MagicLinkConsumeResult {
  /**
   * The just-authenticated user, or `null` when `twoFactorRequired` is true (no
   * session yet — the server set the `ca_2fa_pending` cookie instead). Runs in a
   * guest context, so it can't select the self-only `roles` field — the store
   * reloads `me` after every sign-in to pick those up.
   */
  user: AuthUser | null;
  twoFactorRequired: boolean;
}

/** Request a passwordless sign-in email (a click-through link + a 6-digit code). */
export function requestMagicLink(email: string): Promise<{ requestMagicLink: { success: boolean } }> {
  return gql(
    `mutation RequestMagicLink($input: RequestMagicLinkInput!) {
       requestMagicLink(input: $input) { success }
     }`,
    { input: { email } },
  );
}

/** Consume the 6-digit code the user typed back into the sign-in form. */
export function consumeMagicLinkByCode(
  email: string,
  code: string,
): Promise<{ consumeMagicLinkByCode: MagicLinkConsumeResult }> {
  return gql(
    `${AUTH_USER_FRAGMENT}
     mutation ConsumeMagicLinkByCode($input: ConsumeMagicLinkByCodeInput!) {
       consumeMagicLinkByCode(input: $input) { user { ...AuthUser } twoFactorRequired }
     }`,
    { input: { email, code } },
  );
}

/** Consume a magic-link token (the click-through link the email points at). */
export function consumeMagicLinkByToken(
  token: string,
): Promise<{ consumeMagicLinkByToken: MagicLinkConsumeResult }> {
  return gql(
    `${AUTH_USER_FRAGMENT}
     mutation ConsumeMagicLinkByToken($input: ConsumeMagicLinkByTokenInput!) {
       consumeMagicLinkByToken(input: $input) { user { ...AuthUser } twoFactorRequired }
     }`,
    { input: { token } },
  );
}

export function fetchMe(): Promise<{ me: AuthUser }> {
  // `roles` is selected only here — see the note on AuthUser.roles.
  return gql(
    `${AUTH_USER_FRAGMENT}
     query Me { me { ...AuthUser roles { id title } } }`,
  );
}

export function logout(): Promise<{ logout: boolean }> {
  return gql(`mutation Logout { logout }`);
}

export function validateOtp(token: string): Promise<{ validateOtp: { user: AuthUser } }> {
  return gql(
    `${AUTH_USER_FRAGMENT}
     mutation ValidateOtp($input: ValidateOtpInput!) {
       validateOtp(input: $input) { user { ...AuthUser } }
     }`,
    { input: { token } },
  );
}

export function validateOtpWithRecoveryCode(
  code: string,
): Promise<{ validateOtpWithRecoveryCode: { user: AuthUser } }> {
  return gql(
    `${AUTH_USER_FRAGMENT}
     mutation ValidateOtpWithRecoveryCode($input: ValidateOtpWithRecoveryCodeInput!) {
       validateOtpWithRecoveryCode(input: $input) { user { ...AuthUser } }
     }`,
    { input: { code } },
  );
}

export function configureOtp(
  userId: string,
): Promise<{ configureOtp: { user: { id: string }; secret: string; otpAuthUrl: string } }> {
  return gql(
    `mutation ConfigureOtp($input: ConfigureOtpInput!) {
       configureOtp(input: $input) { user { id } secret otpAuthUrl }
     }`,
    { input: { userId } },
  );
}

export function verifyOtp(
  userId: string,
  token: string,
): Promise<{ verifyOtp: { user: AuthUser; recoveryCodes: string[] } }> {
  return gql(
    `${AUTH_USER_FRAGMENT}
     mutation VerifyOtp($input: VerifyOtpInput!) {
       verifyOtp(input: $input) { user { ...AuthUser } recoveryCodes }
     }`,
    { input: { userId, token } },
  );
}

export function disable2fa(input: {
  userId: string;
  code?: string;
  recoveryCode?: string;
}): Promise<{ disable2fa: { user: AuthUser } }> {
  return gql(
    `${AUTH_USER_FRAGMENT}
     mutation Disable2fa($input: Disable2faInput!) {
       disable2fa(input: $input) { user { ...AuthUser } }
     }`,
    { input },
  );
}

export function regenerateOtpRecoveryCodes(
  code: string,
): Promise<{ regenerateOtpRecoveryCodes: { recoveryCodes: string[]; remaining: number } }> {
  return gql(
    `mutation RegenerateOtpRecoveryCodes($code: String!) {
       regenerateOtpRecoveryCodes(code: $code) { recoveryCodes remaining }
     }`,
    { code },
  );
}
